import React from "react";
import { AppConstant } from "../Utils/AppConstant";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useTranslation } from "react-i18next";

type FooterLink = {
  label: string;
  href: string;
};

type FooterSection = {
  title: string;
  links: FooterLink[];
};

interface FooterLinkSectionProps {
  title: string;
  links: FooterLink[];
}

const FooterLinkSection: React.FC<FooterLinkSectionProps> = ({
  title,
  links,
}) => {

  return (
    <div className="w-full px-4 sm:w-1/2 lg:w-2/12">
      <div className="mb-10 w-full">
        <h4 className="text-dark mb-6 text-lg font-semibold">{title}</h4>
        <ul>
          {links.map((link, index) => (
            <li key={index}>
              <a
                href={link.href}
                className="text-body-color hover:text-primary mb-2 inline-block text-base leading-loose"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

type SocialLink = {
  href: string;
  label: string;
  svg: JSX.Element;
};

const socialLinks: SocialLink[] = [
  {
    href: "https://www.facebook.com/ogefremofficiel",
    label: "Facebook",
    svg: (
      <svg width="8" height="16" viewBox="0 0 8 16" className="fill-current">
        <path d="M7.43902 6.4H6.19918H5.75639V5.88387V4.28387V3.76774H6.19918H7.12906C7.3726 3.76774 7.57186 3.56129 7.57186 3.25161V0.516129C7.57186 0.232258 7.39474 0 7.12906 0H5.51285C3.76379 0 2.54609 1.44516 2.54609 3.5871V5.83226V6.34839H2.10329H0.597778C0.287819 6.34839 0 6.63226 0 7.04516V8.90323C0 9.26452 0.243539 9.6 0.597778 9.6H2.05902H2.50181V10.1161V15.3032C2.50181 15.6645 2.74535 16 3.09959 16H5.18075C5.31359 16 5.42429 15.9226 5.51285 15.8194C5.60141 15.7161 5.66783 15.5355 5.66783 15.3806V10.1419V9.62581H6.13276H7.12906C7.41688 9.62581 7.63828 9.41935 7.68256 9.10968V9.08387V9.05806L7.99252 7.27742C8.01466 7.09677 7.99252 6.89032 7.85968 6.68387C7.8154 6.55484 7.61614 6.42581 7.43902 6.4Z" />
      </svg>
    ),
  },
  {
    href: "http://www.twitter.com/ogefrem",
    label: "Twitter",
    svg: (
      <svg width="16" height="12" viewBox="0 0 16 12" className="fill-current">
        <path d="M14.2194 2.06654L15.2 0.939335C15.4839 0.634051 15.5613 0.399217 15.5871 0.2818C14.8129 0.704501 14.0903 0.845401 13.6258 0.845401H13.4452L13.3419 0.751468C12.7226 0.258317 11.9484 0 11.1226 0C9.31613 0 7.89677 1.36204 7.89677 2.93542C7.89677 3.02935 7.89677 3.17025 7.92258 3.26419L8 3.73386L7.45806 3.71037C4.15484 3.61644 1.44516 1.03327 1.00645 0.587084C0.283871 1.76125 0.696774 2.88845 1.13548 3.59296L2.0129 4.90802L0.619355 4.20352C0.645161 5.18982 1.05806 5.96477 1.85806 6.52838L2.55484 6.99804L1.85806 7.25636C2.29677 8.45401 3.27742 8.94716 4 9.13503L4.95484 9.36986L4.05161 9.93346C2.60645 10.8728 0.8 10.8024 0 10.7319C1.62581 11.7652 3.56129 12 4.90323 12C5.90968 12 6.65806 11.9061 6.83871 11.8356C14.0645 10.2857 14.4 4.41487 14.4 3.2407V3.07632L14.5548 2.98239C15.4323 2.23092 15.7935 1.8317 16 1.59687C15.9226 1.62035 15.8194 1.66732 15.7161 1.6908L14.2194 2.06654Z" />
      </svg>
    ),
  },
];

const SocialIcons: React.FC = () => (
  <div className="mb-6 flex items-center">
    {socialLinks.map((item, index) => (
      <a
        key={index}
        href={item.href}
        aria-label={item.label}
        className="text-dark hover:bg-primary hover:border-primary mr-3 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E5E5] hover:text-dark-500 sm:mr-4 lg:mr-3 xl:mr-4"
      >
        {item.svg}
      </a>
    ))}
  </div>
);

export const Footer: React.FC = () => {
  const { CurrentYear, CompanyName } = AppConstant;
  const { t } = useTranslation()
  const linkSections: FooterSection[] = [
    {
      title: t('our_site'),
      links: [
        { label: t('home'), href: "/home" },
        { label: t('services'), href: "/services/accounts/companies" },
        { label: t('about'), href: "/about" },
        { label: t('help'), href: "/help" },
      ],
    },
    {
      title: t('quick_links'),
      links: [
        { label: t('embassy'), href: "https://ogefremsite.org" },
        { label: t('tra'), href: "https://tra.go.tz" },
        { label: t('bandari'), href: "https://bandari.go.tz" },
        { label: t('latra'), href: "https://latra.go.tz" },
      ],
    },
    // {
    //   title: t('quick_links'),
    //   links: [
    //     { label: "Premium Support", href: "https://ogefrem.com" },
    //     { label: "Our Services", href: "https://ogefrem.com" },
    //     { label: "Know Our Team", href: "https://ogefrem.com" },
    //   ],
    // },
  ];

  return (
    <footer className="relative bottom-0 z-10 pt-10 pb-5 lg:pt-[120px] lg:pb-10 text-white">
      <div className="px-6 container mx-auto">
        <div className="-mx-4 flex justify-between items-start flex-wrap">
          <div className="w-full px-4 sm:w-2/3 lg:w-3/12">
            <div className="w-full">
              <Link to="https://ogefremsite.org" className="flex items-center py-5">
                <Logo height={60} width={60} className="mx-3" />
              </Link>
              {CompanyName}
            </div>
          </div>

          {linkSections.map((section, idx) => (
            <FooterLinkSection key={idx} {...section} />
          ))}

          <div className="w-full px-4 sm:w-1/2 lg:w-3/12">
            <div className="mb-10 w-full">
              <h4 className="text-dark mb-6 text-lg font-semibold">
                {t('follow_us_on')}
              </h4>
              <SocialIcons />
              
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 text-sm">
        <span className="dark:text-gray-400">
          © {t('copyright')} {CurrentYear}. {CompanyName} {t('all_rights_reserved')}.
        </span>
      </div>
    </footer>
  );
};
